import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { config } from '../../config';
import useAppLanguage from '../../hooks/useAppLanguage';
import { LANGUAGES, LanguageCode } from '../../i18n/languages';
import { getLanguageFromPath } from '../../i18n/routing';
import srMessages from '../../i18n/sr.json';

const languages = LANGUAGES;

type LanguageContext = {
  currentLanguage: LanguageCode;
  setLanguage: (language: LanguageCode) => void;
  languages: Readonly<string[]>;
};
export const TranslatedTheme = React.createContext<LanguageContext>({
  currentLanguage: config.fallbackLanguage,
  setLanguage: () => { },
  languages,
});

type ComponentProps = {
  children: ReactNode;
};
export const TranslatedComponent =
  ({ children }: ComponentProps): JSX.Element | null => {
    const [messages, setMessages] = useState<any>(srMessages);
    const [language, setLanguage] = useAppLanguage();
    const location = useLocation();
    const languageFromPath = getLanguageFromPath(location.pathname);
    const isLanguageSynced = !languageFromPath || languageFromPath === language;

    const languageContext: LanguageContext = {
      currentLanguage: language,
      languages,
      setLanguage
    };

    const loadTranslations = useCallback(async () => {
      const translations = await import(`../../i18n/${language}.json`);
      setMessages(translations);
    }, [language]);

    useEffect(() => {
      if (isLanguageSynced) {
        loadTranslations();
      }
    }, [isLanguageSynced, loadTranslations]);

    useEffect(() => {
      if (languageFromPath && languageFromPath !== language) {
        setLanguage(languageFromPath);
      }
    }, [language, languageFromPath, setLanguage]);

    if (!messages || !isLanguageSynced) return null;

    return (
      <IntlProvider messages={messages} locale={language} defaultLocale={config.fallbackLanguage}>
        <Helmet htmlAttributes={{ lang: language }} />
        <TranslatedTheme.Provider value={languageContext}>
          {children}
        </TranslatedTheme.Provider>
      </IntlProvider>
    );
}

type Props = {
  children: ReactNode;
};
export const TranslationsProvider = ({ children }: Props): JSX.Element => {
  return (
    <TranslatedComponent>
      {children}
    </TranslatedComponent>
  );
}
